/**
 * Scope-driven data hook for The Briefing (SYR-68, Phase C).
 *
 * Replaces the Phase-1 `useMarketerViewData` (which read localStorage) — this
 * one is driven by the global MarketerScope context, and overlays the REAL
 * "Briefing AI" narrative (one_thing / the_fix) on top of the deterministic
 * BE-derived `MarketerViewData`, in place of the static placeholder narrative.
 *
 * BE numbers come from the existing calculate endpoint; the narrative from the
 * new `/organisations/:orgId/brands/:brandId/marketer-briefing` endpoint. Both
 * use the same scoped filters so they stay consistent.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useElementAccess } from '../../hooks/useTierAccess';
import {
  useCalculateBrandEquityMutation,
  useGetMarketerBriefingQuery,
  useGetOrgBrandBenchmarksQuery,
} from '../../services/nestjs-base-api';
import type {
  IBrandEquityScoreResponse,
} from '../../services/nestjs-base-api';
import {
  PeriodEnum,
  type ICalculateBrandEquityRequest,
} from '../../services/nestjs-base-api/types';
import { buildMarketerViewData } from './buildMarketerViewData';
import {
  useMarketerScope,
  periodTypeToShortLabel,
} from './scope/MarketerScopeContext';
import type { MarketerViewData } from './types';

export interface BriefingNarrative {
  one_thing?: {
    headline?: string;
    body?: string;
    ctaPrimary?: string;
    ctaSecondary?: string;
    /** The two elements the headline contrasts — drives the Hero meters. */
    contrast?: {
      left?: { element?: string; label?: string };
      right?: { element?: string; label?: string };
    };
  };
  the_fix?: { title?: string; redline?: string; move?: string; expectedEffect?: string };
  /** Phase 2: prioritised fixes & quick wins for the Recommendations tab. */
  recommendations?: {
    recommendations?: Array<{
      type?: 'fix' | 'win';
      title?: string;
      rationale?: string;
      objectives?: Array<{
        task?: string;
        detail?: string;
        impact?: 'Low' | 'Med' | 'High';
        effort?: 'Low' | 'Med' | 'High';
        /** Exact BE element name this objective improves. */
        moves?: string;
      }>;
    }>;
  };
  /** Brief plain-English summary of the 7 boosters — the Overall BE gauge caption. */
  boosters_summary?: { summary?: string };
}

/** Per-element benchmark metric (subset of charts/types BenchmarkMetric). */
export interface BriefingBenchmarkMetric {
  categoryName: string;
  level: number;
  value: number;
  industryMean?: number;
  vsIndustryMean?: number;
}

interface UseBriefingDataResult {
  data: MarketerViewData | undefined;
  narrative: BriefingNarrative | null;
  /** Raw calculate response — needed for Q6 building-blocks insights. */
  response: IBrandEquityScoreResponse | undefined;
  /** Second-pass response WITH per-element AI summaries (undefined until it loads). */
  aiResponse: IBrandEquityScoreResponse | undefined;
  /** True while the AI-summary pass is in flight. */
  aiLoading: boolean;
  /** True while the Briefing AI narrative (one_thing/the_fix/recs) is in flight. */
  narrativeLoading: boolean;
  /** Play ids the marketer deleted from the current recommendations set. */
  dismissedRecommendationIds: string[];
  /** Refetch the narrative (after a card delete / recommendations regenerate). */
  refetchNarrative: () => void;
  /** Per-element benchmark metrics for Q2 "vs market". */
  benchmarkMetrics: BriefingBenchmarkMetric[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

const PERIOD_DAYS: Partial<Record<PeriodEnum, number>> = {
  [PeriodEnum.THIRTY_DAYS]: 30,
  [PeriodEnum.SIXTY_DAYS]: 60,
  [PeriodEnum.NINETY_DAYS]: 90,
  [PeriodEnum.ONE_TWENTY_DAYS]: 120,
  [PeriodEnum.ONE_EIGHTY_DAYS]: 180,
  [PeriodEnum.THREE_SIXTY_FIVE_DAYS]: 365,
};

/**
 * Previous-equivalent-period payload — shifts the window back by one period
 * length so we can show "vs Previous". Mirrors DashboardPage's logic. Returns
 * null for All Time / custom ranges, where there's no comparable prior period.
 */
function previousPeriodPayload(
  payload: ICalculateBrandEquityRequest | undefined,
): ICalculateBrandEquityRequest | null {
  if (!payload) return null;
  const days = PERIOD_DAYS[payload.period as PeriodEnum];
  if (!days) return null;
  const now = new Date();
  const prevTo = new Date(now);
  prevTo.setDate(prevTo.getDate() - (days + 1));
  const prevFrom = new Date(now);
  prevFrom.setDate(prevFrom.getDate() - days * 2);
  return {
    ...payload,
    period: PeriodEnum.DATE_RANGE,
    dateFrom: prevFrom.toISOString().split('T')[0],
    dateTo: prevTo.toISOString().split('T')[0],
  };
}

export function useBriefingData(): UseBriefingDataResult {
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { activeBrand, payload, scope } = useMarketerScope();
  const { hasUnrestrictedAccess, isLoading: accessLoading } = useElementAccess();
  const elementsRestricted = !accessLoading && !hasUnrestrictedAccess;

  const [calculate] = useCalculateBrandEquityMutation();
  const [response, setResponse] = useState<IBrandEquityScoreResponse | undefined>();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  // Previous-equivalent-period score for the "vs Previous" stat (best-effort).
  const [prevScore, setPrevScore] = useState<number | null>(null);
  // Second-pass response WITH AI summaries (slow) — feeds drawer First Thoughts.
  const [aiResponse, setAiResponse] = useState<IBrandEquityScoreResponse | undefined>();
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!payload || !jwtToken || !organisationId || !activeBrand) return;
    setLoading(true);
    setErr(null);
    // BE numbers first. The AI narrative is fetched separately (below) via a
    // cached query, gated on this result so it survives tab remounts.
    calculate({ jwtToken, organisationId, payload })
      .unwrap()
      .then((res) => {
        if (!cancelled) setResponse(res);
      })
      .catch((e) => {
        if (!cancelled) setErr(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [payload, jwtToken, organisationId, activeBrand, calculate]);

  // Briefing AI narrative (one_thing/the_fix/recommendations) as a CACHED query,
  // keyed by brand + scope payload — so returning to The Briefing reads the cache
  // instead of re-fetching and flashing the Hero skeleton. Only worth fetching
  // once a score can be computed: below the response minimum it'd burn an LLM
  // call on thin data, and on a restricted tier the whole Briefing AI is locked
  // behind an upgrade (static placeholders). Skip in both cases to save tokens.
  const canFetchBriefing =
    !!payload &&
    !!jwtToken &&
    !!organisationId &&
    !!activeBrand &&
    !!response &&
    !response.insufficientSampleSize &&
    !elementsRestricted;
  const { data: briefingData, isFetching: briefingFetching, refetch: refetchNarrative } =
    useGetMarketerBriefingQuery(
      {
        jwtToken,
        organisationId,
        brandId: activeBrand?.id ?? 0,
        payload: payload as ICalculateBrandEquityRequest,
      },
      { skip: !canFetchBriefing },
    );
  const narrative: BriefingNarrative | null = canFetchBriefing
    ? briefingData?.narrative ?? null
    : null;
  // Skeleton while the score is still computing, or while the briefing query is
  // in flight without cached data. A cache hit resolves synchronously → no flash.
  const narrativeLoading =
    loading || (canFetchBriefing && briefingFetching && !briefingData);

  // Previous-period score (separate, best-effort — never blocks the main view).
  useEffect(() => {
    let cancelled = false;
    setPrevScore(null);
    const prevPayload = previousPeriodPayload(payload);
    if (!prevPayload || !jwtToken || !organisationId) return;
    calculate({ jwtToken, organisationId, payload: prevPayload })
      .unwrap()
      .then((res) => {
        if (
          !cancelled &&
          res &&
          !res.insufficientSampleSize &&
          res.overallBrandEquityScore != null
        ) {
          setPrevScore(res.overallBrandEquityScore);
        }
      })
      .catch(() => {
        /* no previous-period data — cell stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [payload, jwtToken, organisationId, calculate]);

  // AI summaries (slow second pass, skipAiAnalysis:false). Fills the drawer's
  // First Thoughts once it returns; the headline numbers never wait on it.
  // Server-side the AI result is cached, so it's slow only on first run/scope.
  useEffect(() => {
    let cancelled = false;
    setAiResponse(undefined);
    // Wait for the fast pass and skip entirely on thin data — no point running
    // (paid) element-analysis AI when there's no usable score to summarise.
    if (
      !payload ||
      !jwtToken ||
      !organisationId ||
      !response ||
      response.insufficientSampleSize
    ) {
      setAiLoading(false);
      return;
    }
    setAiLoading(true);
    calculate({
      jwtToken,
      organisationId,
      payload: { ...payload, skipAiAnalysis: false },
    })
      .unwrap()
      .then((res) => {
        if (!cancelled) setAiResponse(res);
      })
      .catch(() => {
        /* AI summaries are best-effort — drawer falls back gracefully */
      })
      .finally(() => {
        if (!cancelled) setAiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payload, jwtToken, organisationId, calculate, response]);

  // Industry benchmark for the Q1 "vs Benchmark" badge.
  const benchmarkPeriod =
    payload?.period === PeriodEnum.DATE_RANGE
      ? PeriodEnum.THIRTY_DAYS
      : payload?.period ?? PeriodEnum.ONE_EIGHTY_DAYS;
  const { data: benchmarkData } = useGetOrgBrandBenchmarksQuery(
    {
      jwtToken,
      brandId: activeBrand?.id ?? 0,
      query: {
        timePeriod: benchmarkPeriod,
        productType: 'all',
        includeHistorical: false,
      },
    },
    { skip: !activeBrand?.id || !jwtToken },
  );

  const benchmarkComparison = useMemo<
    { industryMean: number; sampleSize?: number } | null
  >(() => {
    if (!benchmarkData?.available || !benchmarkData?.data?.brandPerformance?.metrics) {
      return null;
    }
    const overall = benchmarkData.data.brandPerformance.metrics.find(
      (m: { level: number }) => m.level === 0,
    );
    if (!overall || overall.industryMean === undefined) return null;
    return {
      industryMean: overall.industryMean,
      sampleSize: benchmarkData.data.distribution?.sampleSize,
    };
  }, [benchmarkData]);

  const data = useMemo<MarketerViewData | undefined>(() => {
    // Prefer the AI-summary pass once it's in (same numbers + per-element
    // aiSummary for the Q3/Q4 hero-card snippets); fall back to the fast one so
    // numbers render immediately.
    const src = aiResponse ?? response;
    if (!activeBrand || !src) return undefined;
    const built = buildMarketerViewData({
      brand: { name: activeBrand.name },
      response: src,
      benchmarkComparison,
      periodLabel: periodTypeToShortLabel(scope.periodType),
      vsPreviousDiff:
        prevScore != null && src.overallBrandEquityScore != null
          ? src.overallBrandEquityScore - prevScore
          : null,
    });
    // Overlay the REAL Briefing AI narrative on the hero (replaces placeholder).
    if (narrative?.one_thing) {
      if (narrative.one_thing.headline) built.hero.headline = narrative.one_thing.headline;
      if (narrative.one_thing.body) built.hero.body = narrative.one_thing.body;
    }
    // Overlay the AI boosters summary on the Overall BE gauge caption (falls back
    // to the deterministic buildBoostersFootnote when the narrative is absent).
    if (narrative?.boosters_summary?.summary) {
      built.boostersFootnote = narrative.boosters_summary.summary;
    }
    return built;
  }, [activeBrand, response, aiResponse, benchmarkComparison, narrative, scope.periodType, prevScore]);

  const benchmarkMetrics = useMemo<BriefingBenchmarkMetric[]>(() => {
    const metrics = benchmarkData?.data?.brandPerformance?.metrics;
    return Array.isArray(metrics) ? (metrics as BriefingBenchmarkMetric[]) : [];
  }, [benchmarkData]);

  return {
    data,
    narrative,
    response,
    aiResponse,
    aiLoading,
    narrativeLoading,
    dismissedRecommendationIds: briefingData?.dismissedRecommendationIds ?? [],
    refetchNarrative,
    benchmarkMetrics,
    isLoading: loading,
    isError: Boolean(err),
    error: err,
  };
}
